import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  taskService,
  type Task,
  type TaskStatus,
  type CreateTaskPayload,
  type ListTasksParams,
  type ListTasksResult,
  type LinearComment,
} from '../services/task.service';

export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation<Task, Error, CreateTaskPayload>({
    mutationFn: (payload) => taskService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useTasks(params?: ListTasksParams) {
  return useQuery<ListTasksResult>({
    queryKey: ['tasks', params],
    queryFn: () => taskService.list(params),
    staleTime: 15_000,
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation<Task, Error, { id: string; patch: Partial<Task> }>({
    mutationFn: ({ id, patch }) => taskService.update(id, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useSetTaskStatus() {
  const queryClient = useQueryClient();
  return useMutation<Task, Error, { id: string; status: TaskStatus; statusNotes?: string | null }>({
    mutationFn: ({ id, status, statusNotes }) => taskService.setStatus(id, status, statusNotes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useSetTaskAssignee() {
  const queryClient = useQueryClient();
  return useMutation<Task, Error, { id: string; owner: string | null; email?: string | null }>({
    mutationFn: ({ id, owner, email }) => taskService.setAssignee(id, owner, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useTaskComments(id: string | null, enabled = true) {
  return useQuery<LinearComment[]>({
    queryKey: ['task-comments', id],
    queryFn: () => taskService.getComments(id as string),
    enabled: enabled && !!id,
    staleTime: 15_000,
  });
}

export function useAddTaskComment() {
  const queryClient = useQueryClient();
  return useMutation<LinearComment | null, Error, { id: string; body: string }>({
    mutationFn: ({ id, body }) => taskService.addComment(id, body),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['task-comments', id] });
    },
  });
}
